/**
 * 格式化工具函数
 */

/**
 * 格式化数字为K、M、B格式
 * @param {number} num - 要格式化的数字
 * @param {number} digits - 小数位数，默认1位
 * @returns {string} 格式化后的字符串
 * 
 * @example
 * formatNumber(999)      // '999'
 * formatNumber(1000)     // '1K'
 * formatNumber(1234)     // '1.2K'
 * formatNumber(4900)     // '4.9K'
 * formatNumber(12345)    // '12.3K'
 * formatNumber(1234567)  // '1.2M'
 * formatNumber(1000000000) // '1B'
 */
export const formatNumber = (num, digits = 1) => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0'
  }

  const absNum = Math.abs(num)
  const sign = num < 0 ? '-' : ''

  // 千（K）
  if (absNum >= 1000 && absNum < 1000000) {
    const value = absNum / 1000
    // 如果小数部分为0，不显示小数点
    const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(digits)
    return sign + formatted + 'K'
  }
  
  // 百万（M）
  if (absNum >= 1000000 && absNum < 1000000000) {
    const value = absNum / 1000000
    const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(digits)
    return sign + formatted + 'M'
  }
  
  // 十亿（B）
  if (absNum >= 1000000000) {
    const value = absNum / 1000000000
    const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(digits)
    return sign + formatted + 'B'
  }

  // 小于1000，直接显示
  return sign + absNum.toString()
}

/**
 * 格式化数字为中文单位（万、亿）
 * @param {number} num - 要格式化的数字
 * @param {number} digits - 小数位数，默认1位
 * @returns {string} 格式化后的字符串
 * 
 * @example
 * formatNumberCN(999)      // '999'
 * formatNumberCN(10000)    // '1万'
 * formatNumberCN(12345)    // '1.2万'
 * formatNumberCN(100000000) // '1亿'
 */
export const formatNumberCN = (num, digits = 1) => {
  if (num === null || num === undefined || isNaN(num)) {
    return '0'
  }

  const absNum = Math.abs(num)
  const sign = num < 0 ? '-' : ''

  // 万
  if (absNum >= 10000 && absNum < 100000000) {
    const value = absNum / 10000
    const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(digits)
    return sign + formatted + '万'
  }
  
  // 亿
  if (absNum >= 100000000) {
    const value = absNum / 100000000
    const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(digits)
    return sign + formatted + '亿'
  }

  // 小于1万，直接显示
  return sign + absNum.toString()
}

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @param {number} digits - 小数位数
 * @returns {string} 格式化后的字符串
 * 
 * @example
 * formatFileSize(1024)      // '1KB'
 * formatFileSize(1048576)   // '1MB'
 * formatFileSize(1073741824) // '1GB'
 */
export const formatFileSize = (bytes, digits = 1) => {
  if (bytes === 0) return '0B'
  if (!bytes) return '0B'

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  const value = bytes / Math.pow(k, i)
  const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(digits)
  
  return formatted + sizes[i]
}

/**
 * 格式化时间（相对时间）
 * @param {string|Date} date - 日期
 * @returns {string} 相对时间字符串
 * 
 * @example
 * formatTimeAgo(new Date())           // '刚刚'
 * formatTimeAgo('2024-01-01')         // 'X天前'
 */
export const formatTimeAgo = (date) => {
  if (!date) return ''
  
  const now = new Date()
  const targetDate = new Date(date)
  const diff = now - targetDate
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (seconds < 60) return '刚刚'
  if (minutes < 60) return `${minutes}分钟前`
  if (hours < 24) return `${hours}小时前`
  if (days < 30) return `${days}天前`
  if (months < 12) return `${months}个月前`
  return `${years}年前`
}

/**
 * 格式化百分比
 * @param {number} value - 数值（0-1之间）
 * @param {number} digits - 小数位数
 * @returns {string} 百分比字符串
 */
export const formatPercent = (value, digits = 0) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%'
  }
  return (value * 100).toFixed(digits) + '%'
}

